<template>
    <EditableRoot
        v-slot="{ isEditing }"
        :model-value="value ?? ''"
        :placeholder="placeholder ?? fieldConfig.placeholder"
        activation-mode="none"
        submit-mode="both"
        class="profile-editable-field"
        @update:model-value="handleUpdate">
        <i v-if="field === 'job_title'" class="bi bi-briefcase text-base/none shrink-0" aria-hidden="true" />
        <EditableArea class="edit-area">
            <EditablePreview v-if="!isSaving" :class="fieldConfig.previewClass" :data-testid="`${fieldConfig.testIdPrefix}-preview`" />
            <EditableInput class="edit-input" :aria-label="fieldConfig.label" :data-testid="`${fieldConfig.testIdPrefix}-input`" />
        </EditableArea>
        <template v-if="!isSaving">
            <EditableEditTrigger v-if="!isEditing" as-child>
                <BaseButton theme="tertiary" size="sm" icon-only :aria-label="`Edit ${fieldConfig.label}`" :data-testid="`${fieldConfig.testIdPrefix}-edit`">
                    <i class="bi bi-pen" aria-hidden="true" />
                </BaseButton>
            </EditableEditTrigger>
            <EditableSubmitTrigger as-child>
                <BaseButton theme="secondary" size="sm" icon-only :aria-label="`Save ${fieldConfig.label}`" :data-testid="`${fieldConfig.testIdPrefix}-submit`">
                    <i class="bi bi-check text-lg/none" aria-hidden="true" />
                </BaseButton>
            </EditableSubmitTrigger>
            <EditableCancelTrigger as-child>
                <BaseButton theme="tertiary" size="sm" icon-only :aria-label="`Cancel editing ${fieldConfig.label}`">
                    <i class="bi bi-x text-lg/none" aria-hidden="true" />
                </BaseButton>
            </EditableCancelTrigger>
        </template>
        <LoadingSpinner v-else class="size-6 shrink-0" />
    </EditableRoot>
</template>

<script setup lang="ts">
import { getCookie } from "/js/utils.js";
import { router } from "@inertiajs/vue3";
import BaseButton from "~vue/components/BaseButton.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import type { UserProfileEndpoints } from "~vue/UserProfile/types";
import { EditableArea, EditableCancelTrigger, EditableEditTrigger, EditableInput, EditablePreview, EditableRoot, EditableSubmitTrigger } from "reka-ui";
import { computed, ref } from "vue";

const { field, value, endpoints, placeholder } = defineProps<{
    field: "full_name" | "job_title";
    value: string | null;
    endpoints: UserProfileEndpoints;
    placeholder?: string;
}>();

const fieldConfig = computed(() =>
    field === "full_name"
        ? { label: "name", testIdPrefix: "profile-name", previewClass: "body text-ink-muted", refreshProp: "user_name", placeholder: "Enter text..." }
        : { label: "job title", testIdPrefix: "profile-job-title", previewClass: "body-small", refreshProp: "user_profile", placeholder: "" },
);

const isSaving = ref(false);
const handleUpdate = (newValue: string) => {
    if (!newValue?.trim()) {
        return;
    }

    router.post(
        endpoints.profile,
        { detail: field, [field]: newValue },
        {
            headers: {
                "X-CSRFToken": getCookie("csrftoken") ?? "",
                "X-Inertia-Next": endpoints.current_page,
            },
            preserveScroll: true,
            only: [fieldConfig.value.refreshProp, "errors"],
            onStart: () => (isSaving.value = true),
            onFinish: () => (isSaving.value = false),
        },
    );
};
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .profile-editable-field {
        @apply inline-flex min-w-0 max-w-full items-center gap-2;
    }

    .edit-area {
        @apply min-w-0;

        &[data-focused] {
            @apply flex items-center rounded-md border border-control-border bg-control-bg py-1.25 px-2.5 transition-colors focus-within:border-control-border-focus;
        }
    }

    .edit-input {
        @apply min-w-0 w-full border-none bg-transparent outline-none scaffold-body-small text-control-text-strong placeholder:text-control-placeholder;
    }
}
</style>
